import React, { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
 ArrowLeft,
 Download,
 Loader2,
 ListChecks,
 Printer,
 CheckCircle2,
} from 'lucide-react';
import { adminVendorDocumentsApi } from '../../services/adminVendorDocumentsService';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';
import ContractBodyHtml from '../../components/vendor/contract/ContractBodyHtml';
import { downloadContractPdf } from '../../utils/vendorContractPdf';
import { useToastContext } from '../../contexts/ToastContext';
import Button from '../../components/shared/Button';
import { formatDate } from '../../utils/dateUtils';

const AdminVendorContractDetail = () => {
 const { id, contractId } = useParams();
 const { error: showError } = useToastContext();
 const printRef = useRef(null);
 const [pdfLoading, setPdfLoading] = useState(false);

 const { data, isLoading, error } = useQuery({
 queryKey: ['admin-vendor-contract', id, contractId],
 queryFn: () => adminVendorDocumentsApi.getContract(id, contractId),
 enabled: !!id && !!contractId,
 });

 const contract = data?.data;
 const vendorMeta = data?.vendor;

 const handleDownload = async () => {
 if (!contract) return;
 setPdfLoading(true);
 try {
 await downloadContractPdf(contract);
 } catch {
 showError('Download failed', 'Could not generate PDF.');
 } finally {
 setPdfLoading(false);
 }
 };

 if (isLoading) {
 return (
 <div className="space-y-5">
 <div className="py-20 text-center">
 <Loader2 className="w-8 h-8 animate-spin text-ink mx-auto mb-3" />
 <p className="text-sm text-gray-500">Loading contract…</p>
 </div>
 </div>
 );
 }

 if (error || !contract) {
 return (
 <div className="space-y-5">
 <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
 <p className="font-semibold text-red-800 mb-4">Contract not found</p>
 <Link
 to={`/admin/vendors/${id}/contracts`}
 className="inline-flex items-center gap-1.5 text-sm text-ink hover:underline"
 >
 <ArrowLeft className="w-4 h-4" />
 Back to contracts
 </Link>
 </div>
 </div>
 );
 }

 const businessName = vendorMeta?.business_name || contract.vendor?.business_name || 'Vendor';

 return (
 <div className="space-y-5">
 <AdminPageHeader
 title={contract.title || contract.contract_number || 'Contract'}
 subtitle={`${businessName} · ${contract.contract_number || ''} · ${contract.status}`}
 actions={
 <div className="flex flex-wrap items-center gap-2">
 <Link
 to={`/admin/vendors/${id}/contracts`}
 className="inline-flex items-center gap-1.5 whitespace-nowrap shrink-0 px-3.5 py-1.5 text-xs font-medium rounded-md border border-border bg-surface text-ink hover:bg-surface-muted"
 >
 <ArrowLeft className="w-4 h-4 shrink-0" />
 Back to contracts
 </Link>
 <Button
 variant="outline"
 className="rounded-md whitespace-nowrap"
 onClick={() => window.print()}
 >
 <Printer className="w-4 h-4 mr-1.5" />
 Print
 </Button>
 <Button
 className="rounded-md whitespace-nowrap"
 onClick={handleDownload}
 disabled={pdfLoading}
 >
 {pdfLoading ? (
 <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
 ) : (
 <Download className="w-4 h-4 mr-1.5" />
 )}
 PDF
 </Button>
 </div>
 }
 />

 <AdminGuideCallout
 title={ADMIN_PAGE_GUIDES.vendorContractDetail.title}
 steps={ADMIN_PAGE_GUIDES.vendorContractDetail.steps}
 icon={ListChecks}
 />

 <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
 <div className="lg:col-span-1 space-y-3">
 <div className="product-panel p-4 sm:p-5 shadow-sm space-y-3">
 <h3 className="text-sm font-semibold text-ink">Details</h3>
 <dl className="space-y-2.5 text-sm">
 <div>
 <dt className="text-xs text-gray-500">Status</dt>
 <dd className="font-medium capitalize text-ink">{contract.status}</dd>
 </div>
 <div>
 <dt className="text-xs text-gray-500">Client</dt>
 <dd className="font-medium text-ink">{contract.client_name || '—'}</dd>
 {contract.client_email && (
 <dd className="text-gray-500 text-xs mt-0.5">{contract.client_email}</dd>
 )}
 </div>
 <div>
 <dt className="text-xs text-gray-500">Created</dt>
 <dd className="text-ink">
 {contract.created_at ? formatDate(contract.created_at, 'short') : '—'}
 </dd>
 </div>
 {contract.sent_at && (
 <div>
 <dt className="text-xs text-gray-500">Sent</dt>
 <dd className="text-ink">{formatDate(contract.sent_at, 'short')}</dd>
 </div>
 )}
 {contract.viewed_at && (
 <div>
 <dt className="text-xs text-gray-500">Viewed</dt>
 <dd className="text-ink">{formatDate(contract.viewed_at, 'short')}</dd>
 </div>
 )}
 {contract.signed_at && (
 <div>
 <dt className="text-xs text-gray-500">Signed</dt>
 <dd className="inline-flex items-center gap-1.5 text-emerald-700 font-medium">
 <CheckCircle2 className="w-3.5 h-3.5" />
 {formatDate(contract.signed_at, 'short')}
 {contract.signer_name ? ` by ${contract.signer_name}` : ''}
 </dd>
 </div>
 )}
 {contract.booking?.booking_reference && (
 <div>
 <dt className="text-xs text-gray-500">Booking</dt>
 <dd className="text-ink">{contract.booking.booking_reference}</dd>
 </div>
 )}
 </dl>
 </div>
 </div>

 <div className="lg:col-span-2 product-panel p-4 sm:p-6 shadow-sm overflow-hidden">
 <div ref={printRef} className="space-y-4">
 <div className="flex items-start justify-between gap-3 border-b border-border pb-4">
 <div>
 <p className="text-xs uppercase tracking-wide text-gray-400 mb-1">Contract</p>
 <h2 className="text-lg font-bold text-ink">{contract.title}</h2>
 <p className="text-sm text-gray-500 mt-1">{contract.contract_number}</p>
 </div>
 {(contract.logo_url || contract.vendor?.logo_url || vendorMeta?.logo_url) && (
 <img
 src={contract.logo_url || contract.vendor?.logo_url || vendorMeta?.logo_url}
 alt=""
 className="h-12 w-auto object-contain"
 />
 )}
 </div>
 <ContractBodyHtml html={contract.body_html} />
 {contract.signature_data && (
 <div className="pt-4 border-t border-border">
 <p className="text-xs text-gray-500 mb-2">Client signature</p>
 <img src={contract.signature_data} alt="Client signature" className="max-h-24" />
 </div>
 )}
 {contract.vendor_signature_data && (
 <div className="pt-4 border-t border-border">
 <p className="text-xs text-gray-500 mb-2">
 Vendor signature{contract.vendor_signer_name ? ` · ${contract.vendor_signer_name}` : ''}
 </p>
 <img src={contract.vendor_signature_data} alt="Vendor signature" className="max-h-24" />
 </div>
 )}
 </div>
 </div>
 </div>
 </div>
 );
};

export default AdminVendorContractDetail;
